<template>
  <tr :key="facility.id">
    <td :data-order="facility.name" data-search="facility.name">
      <div class="d-flex align-items-center">
        <div v-if="!facility.picture" class="avatar-sm rounded-circle me-2">
          <span class="avatar-title rounded-circle bg-info-lighten text-info">{{ facility.initials }}</span>
        </div>
        <img v-else :src="facility.picture" alt="image" class="img-fluid rounded-circle avatar-sm me-2">
        <a v-if="facility.pending_validation" href="javascript:void(0);" class="link" @click="showPendingValidation">{{ facility.name }}</a>
        <router-link v-else :to="`/installation-edit?id=${facility.id}&action=information`" class="link">{{ facility.name }}</router-link>
      </div>
    </td>
    <td>{{ facility.admin_email }}</td>
    <td>
      <span class="cell-legend">{{ $t('installationRow.locationLegend') }}&nbsp;:</span>
      {{ facility.address }}
    </td>
    <td class="table-date" :data-order="facility.creation_date.getTime()">
      <span class="cell-legend">{{ $t('installationRow.installationDateLegend') }}&nbsp;:</span>
      {{ facility.creation_date.toLocaleDateString($i18n.locale === 'fr' ? 'fr-FR' : 'en-GB') }}
    </td>
    <td class="table-components">
      <BPopover v-if="!facility.blocked_installers && !facility.pending_validation" click :delay="{ show: 0, hide: 0 }" @show="() => onComponentsShow(facility)">
        <template #target>
          <span class="popover-trigger-wrapper" @click="popoverVisible = !popoverVisible">
            <a v-if="facility.alert_counter > 0" href="javascript:void(0);" class="badge bg-danger text-white popover-trigger" tabindex="0">{{ $t('facilityComponents.badge') }}</a>
            <i v-if="facility.alert_counter > 0" class="mdi mdi-alert text-danger ms-1"></i>
            <a v-if="facility.alert_counter == 0" href="javascript:void(0);" class="badge bg-primary text-white popover-trigger" tabindex="0">{{ $t('facilityComponents.badge') }}</a>
          </span>
        </template>
        <div v-for="(component, index) in facility.components" :key="index" class="mb-1">
            <strong><i :class="component.class"></i> {{ $t(component.type) }} :</strong> {{ component.count }}
        </div>
        <div v-if="facility.alert_counter > 0" class="text-danger mt-2">
            <strong>{{ $t('installationRow.hasAlerts') }}</strong><br>
            <router-link :to="`/installation-edit?id=${facility.id}&action=alerts`" class="link link-danger text-decoration-underline">{{ $t('installationRow.viewAlerts') }}</router-link>
        </div>
        <div v-else class="mt-2">
          <router-link :to="`/installation-edit?id=${facility.id}&action=components`" class="link link-primary text-decoration-underline">{{ $t('installationRow.manageList') }}</router-link>
        </div>
      </BPopover>
    </td>
    <td v-if="facility.blocked_installers" width="60">
      <i class="mdi mdi-close-circle text-danger fs-2" :title="$t('installationRow.installerBlocked')" @click="showInstallerBlocked"></i>
    </td>
    <td v-else-if="facility.pending_validation" class="table-action text-center" width="60">
      <!-- Awaiting the administrator validation : the installer can only delete it -->
      <button class="btn btn-sm btn-light dropdown-toggle arrow-none border" data-bs-toggle="dropdown" aria-expanded="false" :title="$t('installationRow.pendingValidationTitle')">
        <i class="mdi mdi-timer-sand text-info"></i>
      </button>
      <div class="dropdown-menu dropdown-menu-end pending-menu">
        <span class="dropdown-item-text text-muted fst-italic small">{{ $t('installationRow.pendingValidation') }}</span>
        <hr class="my-0">
        <button @click="openDeleteModal(facility.id)" class="dropdown-item text-danger">
          <i class="mdi mdi-delete me-1"></i> {{ $t('installationRow.deleteInstallation') }}
        </button>
      </div>
    </td>
    <td v-else class="table-action text-center" width="60">
      <button class="btn btn-sm btn-light dropdown-toggle arrow-none border" data-bs-toggle="dropdown" aria-expanded="false">
        <i class="mdi mdi-dots-vertical"></i>
      </button>
      <div class="dropdown-menu dropdown-menu-end">
        <router-link :to="`/installation-edit?id=${facility.id}&action=information`" class="dropdown-item">
          <i class="mdi mdi-pencil me-1"></i> {{ $t('installationRow.editInfo') }}
        </router-link>
        <router-link :to="`/installation-edit?id=${facility.id}&action=components`" class="dropdown-item">
          <i class="mdi mdi-sitemap me-1"></i> {{ $t('installationRow.components') }}
        </router-link>
        <button @click="openManagerSite" class="dropdown-item">
          <i class="mdi mdi-web me-1"></i> {{ $t('installationRow.managerSpace') }}
        </button>
        <hr class="my-0">
        <button @click="openDeleteModal(facility.id)" class="dropdown-item text-danger">
          <i class="mdi mdi-delete me-1"></i> {{ $t('installationRow.deleteInstallation') }}
        </button>
      </div>
    </td>
  </tr>
</template>

<script>
import { useUserStore } from '@/stores/userStore';
import { BPopover } from 'bootstrap-vue-next';
import { showToast } from '@/services/toast';

export default {
  name: 'InstallationRow',
  components : {
    BPopover
  },
  props: {
    facility: {
      type: Object,
      required: true
    },
    openDeleteModal: {
      type: Function,
      required: true
    },
  },
  methods: {
    openManagerSite() {
      if(this.facility.blocked_managers) {
        showToast('error', this.$t('installationRow.managerBlocked'));
      } else {
        window.open(this.facility.managerURL, '_blank');
      }
    },
    async onComponentsShow(facility) {
      if(facility.hubs == null) {
        const userStore = useUserStore();
        await userStore.loadComponents(facility.id);
      }
    },
    showInstallerBlocked() {
      showToast('error', this.$t('installationRow.installerBlocked'));
    },
    showPendingValidation() {
      showToast('info', this.$t('installationRow.pendingValidation'));
    },
  }
}
</script>

<style scoped>
.pending-menu {
  max-width: 280px;
}

.pending-menu .dropdown-item-text {
  white-space: normal;
}

.popover-trigger-wrapper {
  display: inline-flex !important;
  align-items: center;
  vertical-align: middle;
  position: relative !important;
  z-index: 0 !important;
}

.popover-trigger-wrapper .mdi-alert {
  font-size: 1.25rem;
  line-height: 1;
  margin-left: 4px;
  position: relative;
  top: 1px;
}
</style>
